import {
  Children,
  CSSProperties,
  isValidElement,
  JSX,
  MouseEvent,
  ReactNode,
} from 'react';

import classNames from 'classnames';
import { PrismicNextLink } from '@prismicio/next';
import { LinkField } from '@prismicio/client';
import { resolveLink } from '@/utils';
import buttonStyles from '@/components/Button/button.module.scss';
import style from './cta-button.module.scss';

/** CMS CTA — label text is authored in Prismic; casing must not be altered via CSS. */

type CtaButtonProps = {
  label: string;
  internalLink?: string | null;
  externalLink?: LinkField | null;
  href?: string;
  onClick?: (_event: MouseEvent<HTMLElement>) => void;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'link' | 'custom';
  htmlType?: 'button' | 'submit' | 'reset';
  className?: string;
  style?: CSSProperties;
  fitContent?: boolean;
  disabled?: boolean;
  loading?: boolean;
  externalNewTab?: boolean;
  downloadMedia?: boolean;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaControls?: string;
  ariaExpanded?: boolean;
  id?: string;
  dataTestId?: string;
};

const CtaButton = ({
  label,
  internalLink,
  externalLink,
  href,
  onClick,
  variant = 'primary',
  htmlType = 'button',
  className,
  style: inlineStyle,
  fitContent = false,
  disabled = false,
  loading = false,
  externalNewTab = true,
  downloadMedia = false,
  ariaLabel,
  ariaDescribedBy,
  ariaControls,
  ariaExpanded,
  id,
  dataTestId,
}: CtaButtonProps): JSX.Element => {
  const isDisabled = disabled || loading;
  const shouldFitContent = variant === 'link' || fitContent;
  const resolvedLink = href
    ? { kind: 'internal' as const, props: { href } }
    : resolveLink(internalLink, externalLink, {
        externalNewTab,

        downloadMedia,
      });

  const hasLinkDestination = resolvedLink.kind !== 'empty';
  const opensNewTab =
    'target' in resolvedLink.props && resolvedLink.props.target === '_blank';

  const computedAriaLabel =
    ariaLabel ??
    (opensNewTab ? `${label} (opens in a new tab)` : `Go to ${label}`);

  const classes = classNames(
    buttonStyles.button,
    {
      [buttonStyles[`button_color-${variant}`]]: variant !== 'custom',
      [style.button_color_custom]: variant === 'custom',
    },
    style.cta_button,
    {
      [style.link_variant]: variant === 'link',
      [style.fit_content]: shouldFitContent,
      [style.disabled_link]: hasLinkDestination && isDisabled,
      [style.loading]: loading,
    },
    className,
  );

  const commonProps = {
    id,
    className: classes,
    style: inlineStyle,
    title: label,
    'aria-label': computedAriaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-controls': ariaControls,
    'aria-expanded': ariaExpanded,
    'aria-busy': loading ? true : undefined,
    'data-testid': dataTestId,
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  };

  if (hasLinkDestination) {
    return (
      <PrismicNextLink
        {...commonProps}
        {...resolvedLink.props}
        aria-disabled={isDisabled ? true : undefined}
        onClick={handleClick}
      >
        <span className={buttonStyles.button__content}>{label}</span>
      </PrismicNextLink>
    );
  }

  return (
    <button
      {...commonProps}
      disabled={isDisabled}
      onClick={handleClick}
      type={htmlType}
    >
      <span className={buttonStyles.button__content}>{label}</span>
    </button>
  );
};

export type CtaActionsRowProps = {
  /** Extra classes on the row root — slice SCSS owns width/layout modifiers. */
  className?: string;
  children: ReactNode;
};

const CtaActionsRow = ({
  className,
  children,
}: CtaActionsRowProps): JSX.Element | null => {
  const buttons = Children.toArray(children).filter(isValidElement);

  if (buttons.length === 0) {
    return null;
  }

  return (
    <div className={classNames(style.actions_row, className)}>{children}</div>
  );
};

export { CtaButton, CtaActionsRow };
export type { CtaButtonProps };
